import { createSlice } from "@reduxjs/toolkit";

/**
 * Cloud Catcher Remediation Hub slice.
 *
 * Mirrors the saga + slice pattern used elsewhere in the app
 * (see src/store/cspm/cspmSlice.js + src/store/cspm/saga.js).
 *
 * Each async operation gets a triplet of plain-object action creators:
 *   - <name>         dispatched by the component → trigger for the saga
 *   - <name>Success  dispatched by the saga on 2xx response
 *   - <name>Failure  dispatched by the saga on non-2xx response
 *
 * Components stay declarative: dispatch, then useEffect on the slice flags.
 */

const initialList = {
  items: [],
  next_token: null,
  current_token: null,
  status: "idle", // idle | loading | succeeded | failed
  error: null,
  next_poll_seconds: 60,
  last_fetched_at: null,
};

const initialState = {
  list: initialList,
  detailById: {},
  detailStatus: { status: "idle", error: null, id: null },
  remediableChecks: { ids: [], status: "idle", error: null, fetched_at: null },
  checkPlaybooks: {},
  checkPlaybookStatus: { status: "idle", error: null, check_id: null },
  inventoryByType: {},
  notificationSettings: { data: null, status: "idle", error: null },
  notificationSettingsUpdate: { status: "idle", error: null },
  iamStack: { template: null, role_name: null, filename: null, status: "idle", error: null },
  /**
   * Last completed action envelope. Set on success/failure of any of the
   * imperative operations: create, approve, cancel, run-now, schedule,
   * refresh, provideInputs. Components watch this to fire toasts.
   *
   * Shape: { kind, requestId, succeeded, skipped, error } | null
   *
   * The `requestId` lets a component disambiguate its own dispatch from
   * another component's dispatch of the same kind (incremented per trigger).
   */
  actionFeedback: null,
  actionInProgress: {}, // { [kind]: requestId }
  _nextRequestId: 1,
};

const beginAction = (state, kind) => {
  const requestId = state._nextRequestId++;
  state.actionInProgress[kind] = requestId;
  return requestId;
};

const completeAction = (state, kind, payload) => {
  state.actionFeedback = { kind, ...payload };
  delete state.actionInProgress[kind];
};

export const remediationSlice = createSlice({
  name: "remediation",
  initialState,
  reducers: {
    /* --------------------------- list --------------------------- */
    fetchRequests: (state, action) => {
      state.list.status = "loading";
      state.list.error = null;
      state.list.current_token = action.payload?.next_token ?? null;
      // Background poll passes silent: true — keep rows until fresh data arrives.
      if (!action.payload?.silent) {
        state.list.items = [];
      }
    },
    fetchRequestsSuccess: (state, action) => {
      const { params, status, ...data } = action.payload || {};
      state.list.items = data.items || [];
      state.list.next_token = data.next_token ?? null;
      state.list.current_token = params?.next_token ?? null;
      if (data.next_poll_seconds) {
        state.list.next_poll_seconds = Number(data.next_poll_seconds) || 60;
      }
      state.list.status = "succeeded";
      state.list.error = null;
      state.list.last_fetched_at = Date.now();
    },
    fetchRequestsFailure: (state, action) => {
      state.list.status = "failed";
      state.list.error =
        action.payload?.message || action.payload?.detail || "Failed to load requests.";
    },

    /* --------------------------- detail --------------------------- */
    fetchRequestDetail: (state, action) => {
      state.detailStatus = { status: "loading", error: null, id: action.payload };
    },
    fetchRequestDetailSuccess: (state, action) => {
      const detail = action.payload;
      if (detail?.id != null) state.detailById[detail.id] = detail;
      state.detailStatus = { status: "succeeded", error: null, id: detail?.id ?? null };
    },
    fetchRequestDetailFailure: (state, action) => {
      state.detailStatus = {
        status: "failed",
        error: action.payload || { detail: "Failed to load request." },
        id: state.detailStatus.id,
      };
    },

    /* --------------------------- remediable check list --------------------------- */
    fetchRemediableChecks: (state) => {
      state.remediableChecks.status = "loading";
      state.remediableChecks.error = null;
    },
    fetchRemediableChecksSuccess: (state, action) => {
      state.remediableChecks.ids = action.payload?.remediable_check_ids || [];
      state.remediableChecks.status = "succeeded";
      state.remediableChecks.error = null;
      state.remediableChecks.fetched_at = Date.now();
    },
    fetchRemediableChecksFailure: (state, action) => {
      state.remediableChecks.status = "failed";
      state.remediableChecks.error = action.payload || { detail: "Failed to load remediable checks." };
      // Stamp so saga / UI never auto-retry in a tight loop after CORS / 5xx.
      state.remediableChecks.fetched_at = Date.now();
    },

    /* --------------------------- check playbook --------------------------- */
    fetchCheckPlaybook: (state, action) => {
      state.checkPlaybookStatus = { status: "loading", error: null, check_id: action.payload };
    },
    fetchCheckPlaybookSuccess: (state, action) => {
      const pb = action.payload;
      if (pb?.check_id) state.checkPlaybooks[pb.check_id] = pb;
      state.checkPlaybookStatus = { status: "succeeded", error: null, check_id: pb?.check_id ?? null };
    },
    fetchCheckPlaybookFailure: (state, action) => {
      state.checkPlaybookStatus = {
        status: "failed",
        error: action.payload || { detail: "Failed to load check playbook." },
        check_id: state.checkPlaybookStatus.check_id,
      };
    },

    /* --------------------------- inventory --------------------------- */
    fetchInventory: (state, action) => {
      const { invType } = action.payload || {};
      if (!invType) return;
      if (!state.inventoryByType[invType]) {
        state.inventoryByType[invType] = { items: [], next_token: null, status: "loading", error: null };
      } else {
        state.inventoryByType[invType].status = "loading";
      }
    },
    fetchInventorySuccess: (state, action) => {
      const { invType, data, append } = action.payload || {};
      if (!invType) return;
      const cur = state.inventoryByType[invType] || { items: [] };
      state.inventoryByType[invType] = {
        items: append ? [...(cur.items || []), ...(data?.items || [])] : data?.items || [],
        next_token: data?.next_token ?? null,
        status: "succeeded",
        error: null,
      };
    },
    fetchInventoryFailure: (state, action) => {
      const { invType, error } = action.payload || {};
      if (!invType) return;
      if (!state.inventoryByType[invType]) state.inventoryByType[invType] = { items: [], next_token: null };
      state.inventoryByType[invType].status = "failed";
      state.inventoryByType[invType].error = error;
    },

    /* --------------------------- notification settings --------------------------- */
    fetchNotificationSettings: (state) => {
      state.notificationSettings.status = "loading";
      state.notificationSettings.error = null;
    },
    fetchNotificationSettingsSuccess: (state, action) => {
      state.notificationSettings.data = action.payload;
      state.notificationSettings.status = "succeeded";
      state.notificationSettings.error = null;
    },
    fetchNotificationSettingsFailure: (state, action) => {
      state.notificationSettings.status = "failed";
      state.notificationSettings.error = action.payload;
    },
    updateNotificationSettings: (state) => {
      state.notificationSettingsUpdate = { status: "loading", error: null };
      beginAction(state, "updateNotificationSettings");
    },
    updateNotificationSettingsSuccess: (state, action) => {
      state.notificationSettings.data = action.payload;
      state.notificationSettingsUpdate = { status: "succeeded", error: null };
      completeAction(state, "updateNotificationSettings", { succeeded: [{ data: action.payload }], skipped: [], error: null });
    },
    updateNotificationSettingsFailure: (state, action) => {
      state.notificationSettingsUpdate = { status: "failed", error: action.payload };
      completeAction(state, "updateNotificationSettings", { succeeded: [], skipped: [], error: action.payload });
    },

    /* --------------------------- iam stack --------------------------- */
    fetchIamStack: (state) => {
      state.iamStack.status = "loading";
      state.iamStack.error = null;
    },
    fetchIamStackSuccess: (state, action) => {
      state.iamStack.template = action.payload?.template || null;
      state.iamStack.role_name = action.payload?.role_name || null;
      state.iamStack.filename = action.payload?.filename || null;
      state.iamStack.status = "succeeded";
      state.iamStack.error = null;
    },
    fetchIamStackFailure: (state, action) => {
      state.iamStack.status = "failed";
      state.iamStack.error = action.payload;
    },

    /* --------------------------- action envelopes --------------------------- */
    createRequests: (state) => { beginAction(state, "create"); },
    createRequestsSuccess: (state, action) => {
      completeAction(state, "create", {
        succeeded: action.payload?.succeeded || [],
        skipped: action.payload?.skipped || [],
        error: null,
      });
    },
    createRequestsFailure: (state, action) => {
      completeAction(state, "create", { succeeded: [], skipped: [], error: action.payload });
    },

    approveRequests: (state) => { beginAction(state, "approve"); },
    approveRequestsSuccess: (state, action) => {
      completeAction(state, "approve", {
        succeeded: action.payload?.succeeded || [],
        skipped: action.payload?.skipped || [],
        error: null,
      });
    },
    approveRequestsFailure: (state, action) => {
      completeAction(state, "approve", { succeeded: [], skipped: [], error: action.payload });
    },

    cancelRequests: (state) => { beginAction(state, "cancel"); },
    cancelRequestsSuccess: (state, action) => {
      completeAction(state, "cancel", {
        succeeded: action.payload?.succeeded || [],
        skipped: action.payload?.skipped || [],
        error: null,
      });
    },
    cancelRequestsFailure: (state, action) => {
      completeAction(state, "cancel", { succeeded: [], skipped: [], error: action.payload });
    },

    runNowRequests: (state) => { beginAction(state, "run-now"); },
    runNowRequestsSuccess: (state, action) => {
      completeAction(state, "run-now", {
        succeeded: action.payload?.succeeded || [],
        skipped: action.payload?.skipped || [],
        error: null,
      });
    },
    runNowRequestsFailure: (state, action) => {
      completeAction(state, "run-now", { succeeded: [], skipped: [], error: action.payload });
    },

    scheduleRequests: (state) => { beginAction(state, "schedule"); },
    scheduleRequestsSuccess: (state, action) => {
      completeAction(state, "schedule", {
        succeeded: action.payload?.succeeded || [],
        skipped: action.payload?.skipped || [],
        error: null,
      });
    },
    scheduleRequestsFailure: (state, action) => {
      completeAction(state, "schedule", { succeeded: [], skipped: [], error: action.payload });
    },

    refreshRequests: (state) => { beginAction(state, "refresh"); },
    refreshRequestsSuccess: (state, action) => {
      completeAction(state, "refresh", {
        succeeded: action.payload?.succeeded || [],
        skipped: action.payload?.skipped || [],
        error: null,
      });
    },
    refreshRequestsFailure: (state, action) => {
      completeAction(state, "refresh", { succeeded: [], skipped: [], error: action.payload });
    },

    provideInputs: (state) => { beginAction(state, "inputs"); },
    provideInputsSuccess: (state, action) => {
      completeAction(state, "inputs", {
        succeeded: action.payload?.succeeded || [],
        skipped: action.payload?.skipped || [],
        error: null,
      });
    },
    provideInputsFailure: (state, action) => {
      completeAction(state, "inputs", { succeeded: [], skipped: [], error: action.payload });
    },

    /* --------------------------- utilities --------------------------- */
    clearActionFeedback: (state) => {
      state.actionFeedback = null;
    },
  },
});

export const {
  fetchRequests,
  fetchRequestsSuccess,
  fetchRequestsFailure,
  fetchRequestDetail,
  fetchRequestDetailSuccess,
  fetchRequestDetailFailure,
  fetchRemediableChecks,
  fetchRemediableChecksSuccess,
  fetchRemediableChecksFailure,
  fetchCheckPlaybook,
  fetchCheckPlaybookSuccess,
  fetchCheckPlaybookFailure,
  fetchInventory,
  fetchInventorySuccess,
  fetchInventoryFailure,
  fetchNotificationSettings,
  fetchNotificationSettingsSuccess,
  fetchNotificationSettingsFailure,
  updateNotificationSettings,
  updateNotificationSettingsSuccess,
  updateNotificationSettingsFailure,
  fetchIamStack,
  fetchIamStackSuccess,
  fetchIamStackFailure,
  createRequests,
  createRequestsSuccess,
  createRequestsFailure,
  approveRequests,
  approveRequestsSuccess,
  approveRequestsFailure,
  cancelRequests,
  cancelRequestsSuccess,
  cancelRequestsFailure,
  runNowRequests,
  runNowRequestsSuccess,
  runNowRequestsFailure,
  scheduleRequests,
  scheduleRequestsSuccess,
  scheduleRequestsFailure,
  refreshRequests,
  refreshRequestsSuccess,
  refreshRequestsFailure,
  provideInputs,
  provideInputsSuccess,
  provideInputsFailure,
  clearActionFeedback,
} = remediationSlice.actions;

export default remediationSlice.reducer;
